import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { AppContent, AppPage, useFeatureToggle, ErrorComponents, Tabs, TabPanel } from '@css-xp/base';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import { SubscriptionTableFlatContainer, IncludedProductsSidebar } from '@css-xp/ppc-subscription-table';
import { BillingProfileTable } from '@css-xp/ppc-billing-profile-table';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { PageBackgroundV2 } from '@css-xp/page-background';
import { PPC_PAGE_TITLE, routePaths, STATSIG_FEATURE_GATES, SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { FilterOption, SortType, Maybe } from '@css-xp/graphtypes/src/generated-types-new';
import { getFilterOptions } from '@css-xp/ppc-subscription-table/src/subscription-table-flat';
import { SUBSCRIPTIONS } from './constants/subscriptions';
import './subscription-page-v2.scss';
// need to be removed once M2.0 Feature flag is removed
import './subscription-page.scss';

const allowedSidebars = {
  [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PAGE_SIDEBAR.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
};

export const InternalLicensesPage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  useTitle(PPC_PAGE_TITLE.INTERNAL_USAGE_SUBSCRIPTIONS);
  const [csvFilterOptions, setCsvFilterOptions] = useState<FilterOption[]>(getFilterOptions());
  const [sortOptions, setSortOptions] = useState<Maybe<SortType>>();

  const [isEnabled] = useFeatureToggle();
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );

  const isFutureDatedSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS
  );
  const isInternalUsageLicensesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_INTERNAL_USAGE_LICENSES_SUPPORT);
  const isIULPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_IUL_PHASE_2);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isWildcardBillingProfileSearchEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_WILDCARD_BILLING_PROFILE_SEARCH_HYDRA
  );

  if (!isIULPhase2Enabled) {
    const NotFound = withErrorWrapper(ErrorComponents.NotFound);

    return <NotFound />;
  }

  const renderAppPage = () => {
    return (
      <AppPage className="app-page-v2 subscription-page" allowedSidebars={allowedSidebars}>
        <AppContent>
          <ScreenEventAnalytics
            event={{
              name: AnalyticsEventSource.PARTNER_INTERNAL_LICENSES_SCREEN,
              attributes: {},
            }}
          />
          <Tabs testId="internal-licenses-tabs-container">
            <TabPanel label={SUBSCRIPTIONS.HEADING}>
              <SubscriptionTableFlatContainer
                transactionAccountId={txaId}
                updateCsvFilterOptions={setCsvFilterOptions}
                updateCsvSortOptions={setSortOptions}
                shouldHideCustomiseView={true}
                shouldHideTabs={true}
                isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
                isSupportForExternallyBilledSubscriptionsEnabled={true}
                isFutureDatedSubscriptionsEnabled={isFutureDatedSubscriptionsEnabled}
                isInternalUsageLicensesEnabled={isInternalUsageLicensesEnabled}
                isTWCPhase1Enabled={isTWCPhase1Enabled}
                shouldFetchInternalLicenses={true}
              />
            </TabPanel>
            <TabPanel label="Billing profiles">
              <BillingProfileTable
                transactionAccountId={txaId}
                shouldFetchInternalLicenses={true}
                isWildcardSearchEnabled={isWildcardBillingProfileSearchEnabled}
              />
            </TabPanel>
          </Tabs>
        </AppContent>
      </AppPage>
    );
  };

  const breadcrumbsItems = [
    {
      title: 'Home',
      href: routePaths.home,
    },
    {
      title: SUBSCRIPTIONS.INTERNAL_USAGE_SUBSCRIPTIONS,
      href: routePaths.internalUsageSubscriptions,
    },
  ];

  return (
    <PageBackgroundV2
      heading={SUBSCRIPTIONS.INTERNAL_USAGE_SUBSCRIPTIONS}
      breadcrumbsProps={{ items: breadcrumbsItems }}
    >
      {renderAppPage()}
    </PageBackgroundV2>
  );
};
